// src/components/modals/CartModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import { saveCart } from '../../utils/cart';
import api from "../../utils/api";
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import { verifyCheckout } from '../../services/negotiationService';
import { getStoredPersonalUser } from '../../utils/personalLogin';
import { useAddressBook } from '../../contexts/AddressBookContext';
import AddressBookModal from './AddressBookModal';

const CartModal = ({ show, onHide, cart, updateQuantity, removeFromCart, cartTotal, store, clearCart, companyId }) => {
  const { currentUser } = useAuth();
  const { addresses, selectedAddress, setSelectedAddress, saveNewAddress, loadAddresses, loadingAddresses } = useAddressBook();
  // The cart is scoped to the current store only (not a global cart)
  const cartStoreId = companyId || store?.username || store?.company_id || null;
  const [deliveryOption, setDeliveryOption] = useState('pickup');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [currentView, setCurrentView] = useState('cart');
  const [isAnimating, setIsAnimating] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  
  // Digital product states
  const [hasDigitalProducts, setHasDigitalProducts] = useState(false);
  const [hasPhysicalProducts, setHasPhysicalProducts] = useState(false);

  // Constants
  const LISTING_TYPES = {
    PRODUCT: 'product',
    SERVICE: 'service'
  };

  // Helper functions
  const getListingTypeDisplay = useCallback((product) => {
    const uploadType = product.upload_type || LISTING_TYPES.PRODUCT;
    return uploadType === LISTING_TYPES.SERVICE ? 'Service' : 'Product';
  }, []);

  const isDigitalProduct = useCallback((product) => {
    return product.is_digital === true || product.is_digital === 'true' || product.is_digital === 1;
  }, []);

  // NEW: Helper to get variation display name
  const getVariationDisplayName = (type) => {
    const names = {
      'color': 'Color',
      'size': 'Size',
      'material': 'Material',
      'pattern': 'Pattern',
      'style': 'Style'
    };
    return names[type.toLowerCase()] || type;
  };

  // NEW: Helper to format selected variations for display
  const formatSelectedVariations = (variations) => {
    if (!variations || typeof variations !== 'object') return null;
    
    return Object.entries(variations)
      .map(([type, variation]) => {
        if (!variation) return null;
        return `${getVariationDisplayName(type)}: ${variation.variation_value}`;
      })
      .filter(Boolean)
      .join(', ');
  };

  // NEW: Helper to get unique cart item key including variations
  const getCartItemKey = (item) => {
    if (!item.variations || Object.keys(item.variations).length === 0) {
      return item.id;
    }
    
    const variationKey = Object.entries(item.variations)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, variation]) => `${type}:${variation?.variation_id}`)
      .join('|');
    
    return `${item.id}|${variationKey}`;
  };

  // Helper to compose a single-line address from an address-book entry
  const formatAddress = (addr) => {
    if (!addr) return '';
    return [addr.streetAddress, addr.city, addr.state, addr.country].filter(Boolean).join(', ');
  };

  const getDeliveryOptions = useCallback((product) => {
    if (isDigitalProduct(product)) return null;
    
    if (!product?.delivery_options) return null;
    
    try {
      if (typeof product.delivery_options === 'string') {
        return JSON.parse(product.delivery_options);
      }
      return product.delivery_options;
    } catch (error) {
      console.error('Error parsing delivery options:', error);
      return null;
    }
  }, [isDigitalProduct]);

  const hasNegotiablePricing = useCallback((product) => {
    if (product?.is_negotiable && 
        product?.starting_price > 0 && 
        product?.ending_price > 0 &&
        product?.starting_price < product?.ending_price) {
      return true;
    }
    return false;
  }, []);

  const getPriceDisplay = useCallback((product) => {
    const basePrice = product?.per_unit_cost || 0;
    const agreed = product?.agreed_price;
    
    if (agreed) {
      return `₦${Number(agreed).toLocaleString()}`;
    }
    if (hasNegotiablePricing(product)) {
      return `₦${product.starting_price?.toLocaleString()} - ₦${product.ending_price?.toLocaleString()}`;
    }
    
    return `₦${basePrice.toLocaleString()}`;
  }, [hasNegotiablePricing]);

  const getProductTypeBadge = useCallback((product) => {
    if (isDigitalProduct(product)) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
          Digital
        </span>
      );
    }
    
    const listingType = getListingTypeDisplay(product);
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
        listingType === 'Service' 
          ? 'bg-orange-100 text-orange-800' 
          : 'bg-blue-100 text-blue-800'
      }`}>
        {listingType}
      </span>
    );
  }, [isDigitalProduct, getListingTypeDisplay]);

  const getConditionLabel = (condition) => {
    if (!condition) return 'Not specified';
    switch (condition) {
      case 'new': return 'Brand New';
      case 'used': return 'Used - Like New';
      case 'refurbished': return 'Refurbished';
      default: return condition;
    }
  };

  const getDeliveryTimelineLabel = (timeline) => {
    if (!timeline) return 'Not specified';
    
    const today = new Date();
    const deliveryDate = new Date();
    deliveryDate.setDate(today.getDate() + parseInt(timeline));
    
    const formattedDate = deliveryDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    
    switch (timeline) {
      case '1': return `1 day (${formattedDate})`;
      case '2': return `2 days (${formattedDate})`;
      case '3': return `3 days (${formattedDate})`;
      case '7': return `1 week (${formattedDate})`;
      case '14': return `14 days (${formattedDate})`;
      case '21': return `21 days (${formattedDate})`;
      default: return `${timeline} days (${formattedDate})`;
    }
  };

  const formatCurrency = (amount, currency = 'NGN') => {
    console.log(amount)
    if (!amount) return currency === 'NGN' ? '₦0.00' : '$0.00';
    try {
      const options = {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      };
      return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', options).format(amount);
    } catch (error) {
      return currency === 'NGN' ? `₦${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
    }
  };

  // Check for digital and physical products
  useEffect(() => {
    const digitalItems = cart.some(item => isDigitalProduct(item));
    const physicalItems = cart.some(item => !isDigitalProduct(item));
    
    setHasDigitalProducts(digitalItems);
    setHasPhysicalProducts(physicalItems);
    
    // Auto-set delivery option for digital-only orders
    if (digitalItems && !physicalItems) {
      setDeliveryOption('digital');
    }
  }, [cart, isDigitalProduct]);

  // Calculate delivery fees - skip for digital products
  const calculateDeliveryFees = useCallback(() => {
    if (hasDigitalProducts && !hasPhysicalProducts) return 0;
    
    let totalDeliveryFee = 0;
    
    cart.forEach(item => {
      if (isDigitalProduct(item)) return;
      
      const deliveryOptions = getDeliveryOptions(item);
      if (deliveryOptions?.delivery?.available && deliveryOption === 'delivery') {
        const fee = deliveryOptions.delivery.fees?.lagos || 0;
        totalDeliveryFee += fee * item.quantity;
      }
    });
    
    return totalDeliveryFee;
  }, [cart, deliveryOption, getDeliveryOptions, hasDigitalProducts, hasPhysicalProducts, isDigitalProduct]);

  // Get final total including delivery (0 for digital-only)
  const finalTotal = cartTotal + calculateDeliveryFees();



  // Save cart to current store's storage whenever it changes
  useEffect(() => {
    if (!cartStoreId) return;

    if (cart.length > 0) {
      saveCart(cart, cartStoreId);
    } else {
      saveCart([], cartStoreId);
    }
  }, [cart, cartStoreId]);



  // Reset payment states when modal is shown/hidden
  useEffect(() => {
    if (show && paymentLink) {
      setIframeLoaded(false);
      setPaymentError(null);
    }
  }, [show, paymentLink]);

  // Address book: fetch the personal user's latest saved addresses every time
  // the cart opens so checkout always reflects mobile-app changes. Guarded so
  // a stale module (mid hot-reload) can't crash the modal.
  useEffect(() => {
    if (!show || typeof loadAddresses !== 'function') return;
    loadAddresses();
  }, [show, loadAddresses]);

  // SSO: auto-fill contact details are provided by the selected delivery
  // details entry, so nothing to prefill here.

  // Enhanced delivery option change handler
  const handleDeliveryOptionChange = (option) => {
    setDeliveryOption(option);
  };

  // Enhanced checkout validation
  const handleProceedToCheckout = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentView('checkout');
      setIsAnimating(false);
    }, 300);
  };

  const handleConfirmOrder = async () => {
    // Contact details come from the selected delivery-details entry, falling
    // back to the stored personal user when the entry lacks them.
    const personalUser = getStoredPersonalUser();
    const contactName = selectedAddress?.fullName || personalUser?.fullname || '';
    const contactEmail = selectedAddress?.email || personalUser?.email || '';
    const contactPhone = selectedAddress?.phone || personalUser?.phoneNumber || '';

    if (!contactName || !contactEmail) {
      showToast('Please add your delivery details (full name and email)', 'error');
      setCurrentView('address-list');
      return;
    }
    setIsProcessing(true);
    try {
      // Verify negotiated prices before checkout
      const verifyItems = cart.map(item => ({
        product_id: item.id || item.product_id,
        requested_amount: Number(item.finalPrice)
      }));
      const userId = currentUser?.company_id || sessionStorage.getItem('negotiationGuestId');
      const verifyResult = await verifyCheckout(verifyItems, userId);

      if (verifyResult.status === 'error') {
        throw new Error(verifyResult.error || 'Failed to verify negotiations');
      }

      // Check for price mismatches — warn but allow
      const mismatches = (verifyResult.results || []).filter(r => r.status === 'price_mismatch');
      if (mismatches.length > 0) {
        console.warn('Price mismatches detected:', mismatches);
        showToast('Some prices have changed since negotiation. Using latest agreed prices.', 'warning');
      }

      // Build agreed price map for verified items
      const agreedPriceMap = {};
      for (const r of verifyResult.results || []) {
        if (r.status === 'verified') {
          agreedPriceMap[r.product_id] = r.agreed_amount;
        }
      }

      // Prepare order data for API - UPDATED for grouped variations
      const orderItems = cart.map(item => {
        const productId = item.id || item.product_id;
        const negotiatedPrice = agreedPriceMap[productId];
        return {
          productId: productId,
          productName: item.product_name,
          product_image: item.product_image,
          price: negotiatedPrice || Number(item.finalPrice),
          original_price: Number(item.finalPrice),
          quantity: Number(item.quantity),
          variations: item.variations,
          delivery_timeline: item.delivery_timeline,
          condition: item.condition,
          is_negotiable: item.is_negotiable,
          starting_price: item.starting_price,
          ending_price: item.ending_price,
          upload_type: item.upload_type,
          delivery_options: item.delivery_options,
          is_digital: isDigitalProduct(item)
        };
      });

      const deliveryFee = calculateDeliveryFees();
      const itemsTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const orderFinalTotal = itemsTotal + deliveryFee;

      // Buyer identity for persisting the delivery address to the address book.
      const personalUser = getStoredPersonalUser();
      const buyerCompanyId = personalUser?.company_id || currentUser?.company_id || null;

      const orderData = {
        items: orderItems,
        customer: {
          name: contactName,
          email: contactEmail,
          phone: contactPhone || '',
          fullName: selectedAddress?.fullName || contactName || '',
          streetAddress: deliveryOption === 'delivery' ? (selectedAddress?.streetAddress || '') : '',
          city: deliveryOption === 'delivery' ? (selectedAddress?.city || '') : '',
          state: deliveryOption === 'delivery' ? (selectedAddress?.state || '') : '',
          country: deliveryOption === 'delivery' ? (selectedAddress?.country || '') : '',
          address: deliveryOption === 'delivery' ? formatAddress(selectedAddress) : '',
          additionalInfo: ''
        },
        deliveryOption: hasPhysicalProducts ? deliveryOption : 'digital',
        deliveryFee: deliveryFee,
        finalTotal: orderFinalTotal,
        hasDigitalProducts: hasDigitalProducts,
        hasPhysicalProducts: hasPhysicalProducts,
        storeName: store?.company_name,
        buyer_company_id: buyerCompanyId
      };

    
      // Send order to backend API
      const response = await api.post(API_CONFIG.ORDERS.CREATE_ORDER, orderData);

      if (response.data.success) {
        setPaymentLink(response.data.paymentLink);
        setOrderId(response.data.orderId);
        showToast('Order created successfully! Proceed to payment.', 'success');
        
        // Animate to payment view
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentView('payment');
          setIsAnimating(false);
        }, 300);
      } else {
        throw new Error(response.data.error || 'Failed to create order');
      }

    } catch (error) {
      console.error('Order creation error:', error);
      showToast(error.response?.data?.error || error.message || 'Failed to create order. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Poll for payment status
  useEffect(() => {
    let pollInterval;
    
    if (currentView === 'payment' && paymentLink && orderId) {
      pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`${API_CONFIG.ORDERS.CHECK_PAYMENT_STATUS}?order=${orderId}`);
          
          // Check if payment is successful
          if (response.data.status === 'paid') {
            const paymentResponse = {
              orderId: response.data.orderId,
              status: response.data.status,
              digitalProducts: response.data.digitalProducts || [],
              hasDigitalProducts: response.data.hasDigitalProducts || false,
              hasPhysicalProducts: response.data.hasPhysicalProducts || false,
              customerEmail: response.data.customerEmail,
              totalAmount: response.data.totalAmount
            };
            
            handlePaymentComplete(true, paymentResponse);
            clearInterval(pollInterval);
          } else if (response.data.status === 'failed') {
            handlePaymentComplete(false, response.data);
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      }, 3000);
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentView, paymentLink, orderId]);

  // Helper function to complete order cleanup
  const completeOrderCleanup = async () => {
    await saveCart([], cartStoreId);
    setPaymentLink(null);
    clearCart();
    setCurrentView('cart');
  };

  // Enhanced payment complete handler
  const handlePaymentComplete = async (success = true, paymentResponse = null) => {
    if (success) {
      await completeOrderCleanup();
      
      onHide();
      
      // Then redirect to success page
      setTimeout(() => {
        const successUrl = `/orders-success/${paymentResponse.orderId}`;
        window.location.href = successUrl;
      }, 100);
      
    } else {
      // Payment failed - stay in modal
      setPaymentLink(null);
      setCurrentView('checkout');
      showToast('Payment was not completed.', 'info');
    }
  };

  const handleBackToCart = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentView('cart');
      setPaymentLink(null);
      setIsAnimating(false);
    }, 300);
  };

  const handleBackToCheckout = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentView('checkout');
      setIsAnimating(false);
    }, 300);
  };

  const openPaymentInNewTab = () => {
    window.open(paymentLink, '_blank');
  };

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setPaymentError(null);
  };

  const handleIframeError = () => {
    console.error('Failed to load payment iframe');
    setPaymentError('Failed to load payment page. Please try again.');
  };

  // Close modal when clicking outside (only for cart and checkout views)
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && currentView !== 'payment') {
      onHide();
    }
  };

  if (!show) return null;

  // Animation classes
  const animationClass = isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100';
  const transitionClass = 'transition-all duration-300 ease-in-out';

  // Payment Link View with iframe
  if (currentView === 'payment' && paymentLink) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
        <div className={`modal-dialog bg-white rounded-xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden ${transitionClass} ${animationClass}`}>
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Complete Payment</h2>
            <button onClick={() => handlePaymentComplete(false)} className="text-gray-400 hover:text-gray-600 text-xl">
              &times;
            </button>
          </div>

          {/* Loading/Error State */}
          {!iframeLoaded && !paymentError && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading payment gateway...</p>
              </div>
            </div>
          )}
          
          {paymentError && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-4">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <p className="text-red-500 mb-4">{paymentError}</p>
                <div className="flex space-x-4 justify-center">
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={openPaymentInNewTab}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                  >
                    Open in New Tab
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Iframe for Payment */}
          <div className={`flex-grow ${!iframeLoaded ? 'hidden' : ''}`}>
            <iframe
              src={paymentLink}
              title="Payment Gateway"
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation"
              allow="payment *"
            />
          </div>
          
          {/* Footer with instructions */}
          <div className="px-4 py-3 bg-gray-100 border-t">
            <p className="text-sm text-gray-600 text-center">
              Complete your payment in the secure form above. Do not close this window until completed.
            </p>
            {hasDigitalProducts && (
              <p className="text-sm text-blue-600 text-center mt-2">
                Digital products will be available for immediate download after payment.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Order Checkout View
  if (currentView === 'checkout') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleBackdropClick}>
        <div className={`modal-dialog bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col overflow-hidden ${transitionClass} ${animationClass}`} onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
            <h2 className="text-2xl font-bold text-gray-900">Confirm Your Order</h2>
            <button onClick={handleBackToCart} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 flex-1 min-h-0 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cart.map(item => {
                  const selectedVariations = formatSelectedVariations(item.variations);
                  
                  return (
                    <div key={getCartItemKey(item)} className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {item.product_image && (
                            <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                          
                          {/* Display selected variations */}
                          {selectedVariations && (
                            <p className="text-xs text-blue-600 font-medium mt-1">
                              {selectedVariations}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-2 mt-1">
                            {getProductTypeBadge(item)}
                            {hasNegotiablePricing(item) && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                Negotiable
                              </span>
                            )}
                          </div>
                          {isDigitalProduct(item) && (
                            <p className="text-xs text-purple-600 mt-1">✓ Instant digital download</p>
                          )}
                        </div>
                      </div>
                      <p className="font-semibold">
                        {hasNegotiablePricing(item) ? getPriceDisplay(item) : formatCurrency(item.finalPrice * item.quantity, item.currency)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Delivery Details - the address book lives on this checkout screen */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Delivery Details</h3>
                {selectedAddress && (
                  <button
                    onClick={() => setCurrentView('address-list')}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Change address
                  </button>
                )}
              </div>

              {hasPhysicalProducts && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                        deliveryOption === 'pickup'
                          ? 'border-purple-700 bg-purple-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-purple-500'
                      }`}
                      onClick={() => handleDeliveryOptionChange('pickup')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                          deliveryOption === 'pickup'
                            ? 'border-purple-700 bg-purple-700'
                            : 'border-gray-300'
                        }`}>
                          {deliveryOption === 'pickup' && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                          )}
                        </div>
                        <div>
                          <h5 className="font-medium text-purple-900">Customer Pickup</h5>
                          <p className="text-purple-700 mt-0.5 text-xs">Pick up from seller's location</p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                        deliveryOption === 'delivery'
                          ? 'border-purple-700 bg-purple-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-purple-500'
                      }`}
                      onClick={() => handleDeliveryOptionChange('delivery')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                          deliveryOption === 'delivery'
                            ? 'border-purple-700 bg-purple-700'
                            : 'border-gray-300'
                        }`}>
                          {deliveryOption === 'delivery' && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                          )}
                        </div>
                        <div>
                          <h5 className="font-medium text-purple-900">Home Delivery</h5>
                          <p className="text-purple-700 mt-0.5 text-xs">Delivered to your address</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {calculateDeliveryFees() > 0 && (
                    <div className="flex justify-between items-center mt-3">
                      <span className="font-medium text-blue-900">Delivery Fee:</span>
                      <span className="font-semibold text-blue-700">
                        {formatCurrency(calculateDeliveryFees(), store.currency || 'NGN')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedAddress ? (
                <div className="bg-white rounded-2xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-blue-900">
                      {selectedAddress.fullName || 'Contact'}
                      {selectedAddress.email ? ` • ${selectedAddress.email}` : ''}
                    </span>
                    <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                      {selectedAddress.label || 'Address'}
                      {selectedAddress.isDefault && (
                        <span className="ml-2 bg-blue-600 text-white px-2 py-0.5 rounded-full">Default</span>
                      )}
                    </span>
                  </div>
                  {selectedAddress.phone && (
                    <p className="text-blue-700 text-sm">{selectedAddress.phone}</p>
                  )}
                  {hasPhysicalProducts && deliveryOption === 'delivery' && (
                    <p className="text-blue-700 mt-1">{formatAddress(selectedAddress)}</p>
                  )}
                  <button
                    onClick={() => setCurrentView('address-list')}
                    className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors inline-flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit Details
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-blue-300 rounded-2xl p-6 text-center">
                  <svg className="w-12 h-12 text-blue-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="font-medium text-blue-900">No delivery details yet</p>
                  <p className="text-sm text-blue-600 mt-1">Add your full name, email, phone and address to continue.</p>
                  <button
                    onClick={() => setCurrentView('address-list')}
                    className="mt-4 inline-flex items-center bg-blue-600 text-white px-5 py-2.5 rounded-full font-medium hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Delivery Details
                  </button>
                </div>
              )}
            </div>

            {/* Digital Products Notice */}
            {hasDigitalProducts && (
              <div className="mb-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-purple-900">Digital Products Included</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Your digital products will be available for immediate download after payment.
                        You'll receive a download PIN via email.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Total with delivery fees */}
            <div className="mb-6 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Subtotal:</span>
                <span className="text-lg font-semibold">
                  {formatCurrency(cartTotal, store.currency || 'NGN')}
                </span>
              </div>
              {calculateDeliveryFees() > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Delivery Fee:</span>
                  <span className="text-lg font-semibold">
                    {formatCurrency(calculateDeliveryFees(), store.currency || 'NGN')}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                <span className="text-xl font-bold">Total:</span>
                <span className="text-2xl font-bold text-blue-700">
                  {formatCurrency(finalTotal, store.currency || 'NGN')}
                </span>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleConfirmOrder}
                disabled={isProcessing}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-colors shadow-md"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Order...
                  </span>
                ) : 'Confirm Order'}
              </button>
              <button
                onClick={handleBackToCart}
                className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Back to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Address Book View - renders the saved-address list (or empty state) and the
  // add-address form as a sub-component inside the SAME modal (no new modal).
  if (currentView === 'address-list' || currentView === 'address-form') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleBackdropClick}>
        <div className={`modal-dialog bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col overflow-hidden ${transitionClass} ${animationClass}`} onClick={(e) => e.stopPropagation()}>
          <AddressBookModal
            key={currentView}
            initialView={currentView === 'address-form' ? 'form' : 'list'}
            addresses={addresses}
            selectedAddress={selectedAddress}
            loadingAddresses={loadingAddresses}
            saveNewAddress={saveNewAddress}
            onSelectAddress={(address) => {
              setSelectedAddress(address);
              handleBackToCheckout();
            }}
            onBack={handleBackToCheckout}
            onCancel={handleBackToCart}
            onSaved={handleBackToCheckout}
          />
        </div>
      </div>
    );
  }

  // Main Cart View - Address section now shown here without input fields
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleBackdropClick}>
      <div className={`modal-dialog bg-white rounded-xl shadow-2xl max-w-6xl w-full flex flex-col overflow-hidden ${transitionClass} ${animationClass}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">Shopping Cart</h2>
          <div className="flex items-center space-x-2">
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="flex items-center gap-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-full px-4 py-2 hover:bg-red-50 hover:text-red-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear Cart
              </button>
            )}
            <button onClick={onHide} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-600">Your cart is empty</p>
              <p className="text-sm text-gray-500 mt-2">
                Items you add from this store will appear here.
              </p>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">Order Summary</h3>
                
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {cart.map(item => {
                    const deliveryOptions = getDeliveryOptions(item);
                    const isNegotiable = hasNegotiablePricing(item);
                    const isDigital = isDigitalProduct(item);
                    const selectedVariations = formatSelectedVariations(item.variations);
                    
                    return (
                      <div key={getCartItemKey(item)} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            {item.product_image ? (
                              <img 
                                src={item.product_image} 
                                alt={item.product_name} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center ${
                                isDigital ? 'bg-purple-100' : 'bg-blue-100'
                              }`}>
                                <span className={`text-lg font-bold ${
                                  isDigital ? 'text-purple-800' : 'text-blue-800'
                                }`}>
                                  {item.product_name?.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">{item.product_name}</h3>
                            
                            {/* Product Type and Details */}
                            <div className="text-xs text-gray-600 space-y-1 mt-1">
                              <div className="flex items-center space-x-2 mb-2">
                                {getProductTypeBadge(item)}
                                {isNegotiable && (
                                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                    Negotiable
                                  </span>
                                )}
                              </div>

                              {/* Display selected variations */}
                              {selectedVariations && (
                                <div className="mb-2">
                                  <p className="text-blue-600 font-medium text-sm">
                                    Selected: {selectedVariations}
                                  </p>
                                  {/* Show price adjustments for variations */}
                                  {item.variations && Object.values(item.variations).some(v => v?.price_adjustment > 0) && (
                                    <div className="text-xs text-green-600 mt-1">
                                      {Object.values(item.variations)
                                        .filter(v => v?.price_adjustment > 0)
                                        .map(variation => (
                                          <span key={variation.variation_id} className="mr-2">
                                            +{formatCurrency(variation.price_adjustment, item.currency)}
                                          </span>
                                        ))
                                      }
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {item.brand && (
                                <p>Brand: {item.brand}</p>
                              )}
                              {item.condition && !isDigital && (
                                <p>Condition: {getConditionLabel(item.condition)}</p>
                              )}
                              {item.delivery_timeline && !isDigital && (
                                <p>Delivery: {getDeliveryTimelineLabel(item.delivery_timeline)}</p>
                              )}
                              
                              {/* Digital Product Notice */}
                              {isDigital && (
                                <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                                  <p className="text-purple-700 text-xs font-medium">
                                    ✓ Instant digital download after payment
                                  </p>
                                </div>
                              )}
                              
                              {/* Delivery Options - Only for physical products */}
                              {!isDigital && deliveryOptions && (
                                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-blue-700 text-xs font-medium mb-1">Delivery Options:</p>
                                  {deliveryOptions.pickup?.available && (
                                    <p className="text-blue-600 text-xs">✓ Pickup Available</p>
                                  )}
                                  {deliveryOptions.delivery?.available && (
                                    <div className="text-blue-600 text-xs">
                                      ✓ Delivery Available
                                      {deliveryOptions.delivery.fees?.lagos > 0 && (
                                        <p className="ml-2">Lagos: ₦{deliveryOptions.delivery.fees.lagos.toLocaleString()}</p>
                                      )}
                                      {deliveryOptions.delivery.fees?.outside_lagos > 0 && (
                                        <p className="ml-2">Outside Lagos: ₦{deliveryOptions.delivery.fees.outside_lagos.toLocaleString()}</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <p className="text-blue-600 font-bold mt-2">
                              {isNegotiable ? getPriceDisplay(item) : formatCurrency(item.finalPrice, item.currency)} each
                            </p>
                            
                            <div className="flex items-center space-x-3 mt-2">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                                >
                                  -
                                </button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                                >
                                  +
                                </button>
                              </div>
                              <button
                                onClick={() => removeFromCart(item.id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-blue-700">
                            {isNegotiable ? getPriceDisplay(item) : formatCurrency(item.finalPrice * item.quantity, item.currency)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Escrow Policy Information */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-amber-800 mb-1">Escrow Protection</h4>
                      <p className="text-xs text-amber-700">
                        Your payment is held securely until you confirm delivery. 
                        {hasDigitalProducts && " Digital products are available immediately after payment."}
                        {hasPhysicalProducts && " You'll receive a unique code via email for physical deliveries."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total and Checkout */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Subtotal:</span>
                      <span className="text-lg font-semibold">
                        {formatCurrency(cartTotal, store.currency || 'NGN')}
                      </span>
                    </div>
                    {calculateDeliveryFees() > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-gray-900">Delivery Fee:</span>
                        <span className="text-lg font-semibold">
                          {formatCurrency(calculateDeliveryFees(), store.currency || 'NGN')}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                      <span className="text-xl font-bold text-gray-900">Total:</span>
                      <span className="text-2xl font-bold text-blue-700">
                        {formatCurrency(finalTotal, store.currency || 'NGN')}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={handleProceedToCheckout}
                      className="flex-1 bg-gradient-to-r rounded-full from-blue-600 to-blue-700 text-white py-3 px-6 font-semibold hover:from-blue-700 hover:to-blue-800 transition-colors shadow-md"
                    >
                      Proceed to Checkout
                    </button>
                    <button
                      onClick={onHide}
                      className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      Continue Shopping
                    </button>
                  </div>

                  {/* Order Process Information */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Order Process:</h4>
                    <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                      <li>Complete checkout and make payment</li>
                      <li>Receive confirmation email with order details</li>
                      {hasDigitalProducts && (
                        <li>Digital products: Receive download PIN via email immediately</li>
                      )}
                      {hasPhysicalProducts && (
                        <>
                          <li>Physical products: Receive unique delivery code via email</li>
                          <li>Seller ships your order</li>
                          <li>Upon delivery, use code to confirm receipt</li>
                        </>
                      )}
                      <li>Seller receives payment only after delivery confirmation</li>
                    </ol>
                  </div>
                </div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartModal;